import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import { expValEquals } from '@confluence/feature-experiments';

// A delivery acknowledgement is not a persisted CAP session. Bound the optimistic
// indicator even when downstream failures cannot be observed by the comment surface.
export const COMMENT_AGENT_STARTUP_TIMEOUT_MS = 30_000;
export type CommentAgentIdentity = {
	agentId?: string | null;
	agentName?: string | null;
	avatarUrl?: string | null;
};

type PendingSession = {
	timer: ReturnType<typeof setTimeout>;
	agentIdentity?: CommentAgentIdentity;
};
const pending = new Map<string, PendingSession>();
const listeners = new Map<string, Set<() => void>>();

export function clearOptimisticCommentAgentSession(commentId: string): void {
	const session = pending.get(commentId);
	if (!session) {
		return;
	}
	clearTimeout(session.timer);
	pending.delete(commentId);
	listeners.get(commentId)?.forEach((listener) => listener());
}

export function startOptimisticCommentAgentSession(
	commentId: string,
	agentIdentity?: CommentAgentIdentity,
): void {
	if (!commentId || pending.has(commentId)) {
		return;
	}
	pending.set(commentId, {
		agentIdentity,
		timer: setTimeout(
			() => clearOptimisticCommentAgentSession(commentId),
			COMMENT_AGENT_STARTUP_TIMEOUT_MS,
		),
	});
	listeners.get(commentId)?.forEach((listener) => listener());
}

export function getOptimisticCommentAgentIdentity(
	commentId: string,
): CommentAgentIdentity | undefined {
	return pending.get(commentId)?.agentIdentity;
}

export function clearOptimisticCommentAgentSessionsForTesting(): void {
	pending.forEach(({ timer }) => clearTimeout(timer));
	pending.clear();
	listeners.clear();
}

function subscribeToComment(commentId: string, listener: () => void): () => void {
	if (!commentId) {
		return () => {};
	}
	const subscribers = listeners.get(commentId) ?? new Set<() => void>();
	listeners.set(commentId, subscribers);
	subscribers.add(listener);
	return () => {
		subscribers.delete(listener);
		if (subscribers.size === 0) {
			listeners.delete(commentId);
			// StrictMode re-subscribes synchronously during effect replay.
			queueMicrotask(() => {
				if (!listeners.has(commentId)) {
					clearOptimisticCommentAgentSession(commentId);
				}
			});
		}
	};
}

export function useOptimisticCommentAgentSession(commentId: string, hasSession: boolean): boolean {
	const subscribe = useCallback(
		(listener: () => void) => subscribeToComment(commentId, listener),
		[commentId],
	);
	const getSnapshot = useCallback(() => pending.has(commentId), [commentId]);
	const isPending = useSyncExternalStore(subscribe, getSnapshot, () => false);

	useEffect(() => {
		if (commentId && hasSession) {
			clearOptimisticCommentAgentSession(commentId);
		}
	}, [commentId, hasSession]);

	return !hasSession && isPending;
}

export function useOptimisticCommentAgentSessions(
	commentIds: readonly string[],
): ReadonlySet<string> {
	// Stable by contents: callers may derive a new ID array from a reply list each render.
	const idsKey = expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
		? JSON.stringify([...new Set(commentIds.filter(Boolean))].sort())
		: '[]';
	const ids: string[] = useMemo(() => JSON.parse(idsKey), [idsKey]);
	const subscribe = useCallback(
		(listener: () => void) => {
			const unsubscribe = ids.map((id) => subscribeToComment(id, listener));
			return () => unsubscribe.forEach((dispose) => dispose());
		},
		[ids],
	);
	const getSnapshot = useCallback(() => JSON.stringify(ids.filter((id) => pending.has(id))), [ids]);
	const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => '[]');
	return useMemo(() => new Set<string>(JSON.parse(snapshot)), [snapshot]);
}
